<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "BesokstypList",
        tabName: "Besökstyper",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KButton from "@/components/Button.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction } from "@/components/KTable/types";
    import { Besokstyp } from "@/tabs/Oppenvard/types";
    import BesokstypForm from "@/tabs/Admin/Oppenvard/BesokstypForm.vue";
    import BesokstypkodList from "@/tabs/Admin/Oppenvard/BesokstypkodList.vue";
    import { useStore } from "@/store";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const columns: Column[] = [
        {
            header: "Typkod",
            field: "typkod",
            sortField: "typkod__displayName",
            sortable: true,
        },
        {
            header: "Namn",
            field: "namn",
            sortField: "namn",
            sortable: true,
        },
        {
            header: "Besöksform",
            field: "form",
            sortField: "form",
            sortable: true,
        },
        {
            header: "Gruppbesök",
            field: "gruppbesok",
            sortField: "gruppbesok",
            sortable: true,
        },
        {
            header: "Teambesök",
            field: "teambesok",
            sortField: "teambesok",
            sortable: true,
        },
        {
            header: "Ordningsnummer",
            field: "ordning",
            sortField: "ordning",
            sortable: true,
        },
        {
            header: "Aktiv",
            field: "aktiv",
            sortField: "aktiv",
            sortable: true,
        },
    ];

    const dropdownActions: KTableAction[] = [
        {
            title: "Redigera besökstyp",
            show: () => true,
            callback: ({ data }) => {
                const besokstyp = data as Besokstyp;

                store.dispatch("tabs/openTab", {
                    component: BesokstypForm,
                    parameters: {
                        besokstypId: besokstyp.id,
                    },
                });
            },
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);

    const filterParams = ref<{ [key: string]: string | number | boolean } | undefined>();
    const subsetName = ref<string>("default");

    const isLoading = ref(false);

    const besokstyper = computed<Besokstyp[]>(() => {
        return store.getters["besokstyp/besokstypSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        }).map((bt: Besokstyp) => {
            return {
                ...bt,
                typkod: `${bt.typkod_display.code} ${bt.typkod_display.displayName}`,
                gruppbesok: bt.gruppbesok ? "Ja" : "Nej",
                teambesok: bt.teambesok ? "Ja" : "Nej",
                aktiv: bt.aktiv === typeof String ? bt.aktiv : bt.aktiv ? "Ja" : "Nej",
            };
        });
    });

    const onPage = async ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        await fetchBesokstyper({ page, limit: rows });
    };

    const onSort = async ({ sortField, sortOrder }: { sortField: string; sortOrder: number }) => {
        const modifiedFilterParams = {
            ...(filterParams.value ?? {}),
        };

        modifiedFilterParams.ordering = sortOrder === -1 ? `-${sortField}` : sortField;

        filterParams.value = modifiedFilterParams;

        await fetchBesokstyper({ page: 1 });
    };

    const fetchBesokstyper = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        isLoading.value = true;

        try {
            const { total, subsetName: fetchedSubsetName } = await store.dispatch("besokstyp/fetchBesokstyper", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta besökstyper", "error");
        }

        isLoading.value = false;
    };

    const openCreateBesokstyp = () => {
        store.dispatch("tabs/openTab", {
            component: BesokstypForm,
        });
    };

    const openBesokstypkoder = () => {
        store.dispatch("tabs/openTab", {
            component: BesokstypkodList,
        });
    };

    onBeforeMount(async () => {
        await fetchBesokstyper({ page: listPage.value });

        await store.dispatch("besokstyp/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besokstyp/removeSubscriber");
    });
</script>

<template>
    <div class="besokstyp-list">
        <admin-lock-border />
        <div class="header">
            <h2>Besökstyper</h2>
            <KButton type="button" variant="hollow" size="slim" @click="openCreateBesokstyp">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Lägg till besökstyp</span>
            </KButton>
            <KButton type="button" variant="link" @click="openBesokstypkoder">
                <span>Kodverk typkod</span>
            </KButton>
        </div>
        <KTable
            reorderableColumns
            paginator
            alwaysShowPaginator
            loadingMessage="Hämtar besökstyper"
            paginatorPosition="bottom"
            name="besokstyp-table"
            :columns="columns"
            :actions="dropdownActions"
            :value="besokstyper"
            :totalRecords="listCount"
            :loading="isLoading"
            @page="onPage"
            @sort="onSort"
        />
    </div>
</template>

<style lang="scss">
    .besokstyp-list {
        .header {
            h2 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
            margin-bottom: 20px;
        }
    }
</style>
